import React, { useState } from "react";
import {
  Box,
  Grid,
  Alert,
  CircularProgress
} from "@mui/material";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  PageBox,
  PageAlert,
  PageHeader,
  FormButton,
  PageLink,
  TwoFaFormGroup,
  TwoFaCodeField
} from "./TwoFaLogin.style";
import { useAuth, ResponseStatus } from "../../providers/Auth";
import { TabTitle } from "../../utils/GeneralFunctions";
import { AlertStateInterface } from "../../interfaces/AlertStateInterface";

const TwoFaLogin = () => {

  const auth = useAuth();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AlertStateInterface;
  TabTitle(t("login.2fa.header.page-title"));
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFAError, setTwoFAError] = useState<string | null>("");
  const [resending, setResending] = useState(false);
  const [firstLanding, setFirstLanding] = useState(true);
  const [invalidAuthCode, setInvalidAuthCode] = useState(false);
  const [sendingForm, setSendingForm] = useState(false);
  const [codeResendSuccess, setCodeResendSuccess] = useState<boolean>(false);
  const [verifiedStatusFailed, setVerifiedStatusFailed] = useState<boolean>(false);

  // Handler for 2FA code submission
  const onSubmit = async (values: { twoFaCode: number }) => {
    setSendingForm(true);
    setFirstLanding(false);
    const code = Array.from({ length: 6 })
        .map((_, idx) => values[`twoFACode${idx}`] || "")
        .join("");

    setTwoFACode(code);

    const verifyResult: any = await auth.verify2FA(code);
    console.log("2FA verification result:", verifyResult);

    if (verifyResult?.status) {
      console.log("2FA verification result:", verifyResult);
    }

    switch (verifyResult?.status) {

        case ResponseStatus.SUCCESS:
          const userProfileResult: any = await auth.getUserProfile();
          console.log("SUCCESS User profile result:", userProfileResult);
          if (userProfileResult.status === ResponseStatus.SUCCESS) {
            navigate("/home");
          } else {
            setVerifiedStatusFailed(true);
            setTwoFAError(t("all.block-form.error.0000"));
          }
          break;

        case ResponseStatus.TWO_FA_ERROR:
          console.log("TWO_FA_ERROR User profile result:", verifyResult);
          setInvalidAuthCode(true);
          setTwoFAError(t("login.2fa.code-invalid"));
          setSendingForm(false);
          setCodeResendSuccess(false);
          break;

        case ResponseStatus.FAIL:
          console.log("FAIL User profile result:", verifyResult);
          setVerifiedStatusFailed(true);
          setTwoFAError(t(verifyResult.message));
          setSendingForm(false);
          setCodeResendSuccess(false);
          break;
        default:
          console.log("2FA verification result:", verifyResult);
          setInvalidAuthCode(false);
          setVerifiedStatusFailed(true);
          setTwoFAError(t("all.block-form.error.0000"));
          setSendingForm(false);
          setCodeResendSuccess(false);
          break;
      }

  };

  const resend2FACode = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, form: any) => {
    e.preventDefault();
    setTwoFAError(null);

    // Reset all twoFACode fields
    setTwoFACode("");
    Array.from({ length: 6 }).forEach((_, idx) => {
      form.change(`twoFACode${idx}`, "");
      const inputs = document.getElementsByName(`twoFACode${idx}`);
      inputs.forEach(input => {
        if (input instanceof HTMLInputElement) {
          input.value = "";
        }
      });
    });
    setResending(true);
    const resendResult: any = await auth.resend2FA();

    setResending(false);
    if (resendResult.status === ResponseStatus.SUCCESS) {
      console.log("2FA code resent successfully");
      setCodeResendSuccess(true);
    } else {
      setCodeResendSuccess(false);
      setTwoFAError(t("login.2fa.code-invalid")); // Error resending code
    }
  };

  return (
    <>
    {state && state.showAlert && (
      <PageAlert
        severity={
          state.severity === "success"
            ? "success"
            : state.severity === "error"
            ? "error"
            : state.severity === "info"
            ? "info"
            : "warning"
        }
      >
        {t(state.message)}
      </PageAlert>
   )}
    <PageBox>
      <PageHeader>
        {t("login.2fa.page-title")}
      </PageHeader>
      { firstLanding && (
        <span>{ t("login.2fa.info") }</span>
      )}
      { twoFAError && (
        <PageAlert severity="error">
          {twoFAError}
        </PageAlert>
      )}
      <Grid item xs={12} sm={6}>
          <Form
            onSubmit={onSubmit}
            initialValues={{ code: twoFACode }}
            render={({ submitError, handleSubmit, submitting, form }) => (
              <form
                onSubmit={handleSubmit}
                autoComplete="off"
                noValidate
              >
                <TwoFaFormGroup row sx={{ justifyContent: "center", mb: 2, gap: "1rem" }}>
                      {[...Array(6)].map((_, idx) => (
                        <TwoFaCodeField
                          key={idx}
                          name={`twoFACode${idx}`}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]",
                            maxLength: 1,
                            style: { textAlign: "center", fontSize: "2rem", width: "4rem", height: "4rem" },
                            autoFocus: idx === 0,
                            tabIndex: idx + 1,
                            'data-idx': idx,
                            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                              const target = e.target as HTMLInputElement;
                              if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Tab" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
                                e.preventDefault();
                              }
                              // Move to previous field on Backspace if empty
                              if (e.key === "Backspace" && !target.value && idx > 0) {
                                const prev = document.querySelector<HTMLInputElement>(`input[data-idx="${idx - 1}"]`);
                                prev?.focus();
                              }
                            },
                            onInput: (e: React.FormEvent<HTMLInputElement>) => {
                              const target = e.target as HTMLInputElement;
                              target.value = target.value.replace(/[^0-9]/g, "");
                            },
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                              const evalue = e.target.value;
                              const val = evalue.replace(/[^0-9]/g, "");
                              // Move to next field if value entered
                              if (val && idx < 5) {
                                const next = document.querySelector<HTMLInputElement>(`input[data-idx="${idx + 1}"]`);
                                next?.focus();
                              }
                            }
                          }}
                          value= { twoFACode[idx]?.length > 0 && twoFACode[idx] !== "" ? twoFACode[idx] : null }
                          variant="standard"
                          required
                          sx={{ mx: 0.5 }}
                        />
                      ))}
                    </TwoFaFormGroup>
                    {invalidAuthCode && (
                      <Box sx={{ mb: 2 }}>
                        { !codeResendSuccess && (
                          <PageLink
                            component={Link}
                            to="#"
                            onClick={e => !resending && resend2FACode(e, form)}
                            variant="body1"
                            color="fuchsiaAccessible.main"
                            style={{ pointerEvents: resending ? "none" : "auto", opacity: resending ? 0.6 : 1 }}
                          >
                            {resending ? t("login.2fa.resend.loading") : t("login.2fa.resend.link")}
                          </PageLink>
                        )}

                        { codeResendSuccess && (
                          <span className="success">{t("login.2fa.resend.success")}</span>
                        )}
                      </Box>
                    )}
                    { verifiedStatusFailed && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        { twoFAError}
                      </Alert>
                    )}
                    <FormButton
                      variant="contained"
                      type="submit"
                      size="large"
                      disabled={submitting}
                      sx={{ mr: theme => theme.spacer_m }}
                      disableElevation
                    >
                      { sendingForm ? (
                        <CircularProgress color="inherit" size="2rem" />
                      ) : (
                        t("login.2fa.button.submit")
                      )}
                    </FormButton>
              </form>
            )}
          >
          </Form>
        </Grid>
        <div style={{ marginBottom: "3rem" }}></div>
    </PageBox>
    <hr />
    </>
  );
};

export default TwoFaLogin;
